import { Environment } from "@omni/types/enums/environment.enum";
import * as LocalStorage from "@omni/utils/localStorage.utils";
import { AxiosError, AxiosInstance, AxiosPromise } from "axios";
import {
    ConfigureOptions,
    Options,
    RefetchOptions,
    ResponseValues
} from "axios-hooks";

import { create as createApi } from "@/api/api";
import {
    ExtendedAxiosRequestConfig,
    ExtendedAxiosResponse,
    ExtendedInternalAxiosRequestConfig
} from "@/api/api.types";
import { getTranslations } from "@/redux/modules/value/translations/translations.selector";
import { Config } from "@/utils/config.utils";
import { ScrollOffsets } from "@/utils/enums";
import { getRequestErrorMessage } from "@/utils/errors";
import { scrollToElementById } from "@/utils/scroll";

export type ExtendedUseAxiosResult<
    TReq = any,
    TBody = any,
    TModel = any,
    TError = any
> = [
    ResponseValues<TModel, TBody, TError>,
    (
        config?: ExtendedAxiosRequestConfig<TReq, TBody, TModel>,
        options?: RefetchOptions
    ) => AxiosPromise<TModel>,
    () => void
];

export interface ExtendedUseAxios {
    <TReq = any, TBody = any, TModel = any, TError = any>(
        config: ExtendedAxiosRequestConfig<TReq, TBody, TModel> | string,
        options?: Options
    ): ExtendedUseAxiosResult<TReq, TBody, TModel, TError>;

    loadCache(data: any[]): void;
    serializeCache(): Promise<any[]>;
    configure(options: ConfigureOptions): void;
    resetConfigure(): void;
    clearCache(): void;
}

type ApiOptions = {
    bff?: boolean;
    cache?: boolean;
};

const defaultApiOptions: ApiOptions = {
    bff: false,
    cache: true
};

export const createApiFor = (
    endPoint: string,
    { bff, cache }: ApiOptions = defaultApiOptions
) => {
    const instance = createApi(endPoint, cache) as AxiosInstance;

    instance.interceptors.request.use(
        (config: ExtendedInternalAxiosRequestConfig) => {
            if (!config.url) return config;

            const currentUrl = new URL(`${config.baseURL}${config.url}`);
            Object.entries(config.urlParams || {}).forEach(([k, v]) => {
                currentUrl.pathname = currentUrl.pathname.replace(`:${k}`, v);
            });

            config.baseURL = `${currentUrl.protocol}//${currentUrl.host}`;
            config.url = currentUrl.pathname;

            if (bff) {
                config.baseURL =
                    Config.general.environment === Environment.Local
                        ? Config.general["bff-base-url"]
                        : `${config.baseURL}/web`;
            }

            return config;
        }
    );

    instance.interceptors.response.use((response: ExtendedAxiosResponse) => {
        if (!response.config.mapper) {
            return response;
        }

        response.data = response.config.mapper(response.data);

        return response;
    });

    return instance;
};

export const handleError = (error: AxiosError<any, any>) => {
    if (error.response && error.response.status === 401) {
        if (Config.general["authentication-required"]) {
            LocalStorage.setItem("previousUrl", location.href);
            location.assign(Config.pages.login);
        }
    }

    const { config, data, status } = error?.response || {};
    const { url, method } = config || {};

    if (config && data && status && url && method) {
        const payload = typeof data === "object" ? data.message : data;
        const strippedUrl = url.split("?")[0];

        const storeState = window.storeManager.getState();
        const dispatch = window.storeManager.dispatch;

        const message = getRequestErrorMessage(
            getTranslations(storeState),
            payload,
            strippedUrl,
            method,
            status
        );

        setTimeout(() => {
            scrollToElementById("errorMessage", ScrollOffsets.DEFAULT, true);
        }, 1000);

        import("@/redux/modules/value/errors/errors.module").then((mod) =>
            dispatch(
                mod.setError({
                    url: strippedUrl,
                    message,
                    method
                })
            )
        );

        return {
            error,
            message
        };
    }

    return { error };
};
